import { AlertTriangle, BotOff, CircleCheck, FileWarning, GitPullRequest, Info, ListChecks, MessageSquareText, ShieldCheck, Target, Undo2 } from "lucide-react"
import type { ReactNode } from "react"
import { openExternalUrl } from "../../../platform/actions"
import type { GroupRenderer, ProviderNoticeGroup } from "../../events/messageGroups"
import { compactInline } from "../../events/presentation"

export function formatProviderNoticeLabel(group: ProviderNoticeGroup): string {
    if ((group.kind === "goal" || group.kind === "recap") && group.body) {
        return `${group.title}: ${compactInline(group.body, 120)}`
    }
    return group.title
}

function noticeColorClass(level: ProviderNoticeGroup["level"]): string {
    switch (level) {
        case "error":
            return "text-error flex-shrink-0"
        case "warning":
            return "text-warning flex-shrink-0"
        case "success":
            return "text-success flex-shrink-0"
        case "info":
            return "text-info flex-shrink-0"
    }
}

function noticeIcon(group: ProviderNoticeGroup): ReactNode {
    const props = { size: "0.9em", className: noticeColorClass(group.level) }
    switch (group.kind) {
        case "goal":
            return <Target {...props} />
        case "rollback":
            return <Undo2 {...props} />
        case "api_error":
        case "model_fallback":
            return <AlertTriangle {...props} />
        case "plan_mode":
        case "plan_mode_exit":
            return <ListChecks {...props} />
        case "pull_request":
            return <GitPullRequest {...props} />
        case "recap":
            return <MessageSquareText {...props} />
        case "read_truncation":
            return <FileWarning {...props} />
        case "agents_killed":
            return <BotOff {...props} />
        case "permission_mode":
            return <ShieldCheck {...props} />
        case "informational":
            return group.level === "success" ? <CircleCheck {...props} /> : <Info {...props} />
    }
}

function formatMetadataValue(value: unknown): string {
    if (typeof value === "string") return value
    if (typeof value === "number") return value.toLocaleString()
    if (typeof value === "boolean") return value ? "yes" : "no"
    return compactInline(JSON.stringify(value) ?? String(value), 240)
}

function ProviderNoticeContent({ group }: { group: ProviderNoticeGroup }) {
    const url = group.url
    return (
        <div className="space-y-2 bg-base-100 px-3 py-2 text-xs">
            {group.body && <div className="whitespace-pre-wrap text-base-content">{group.body}</div>}
            {url && (
                <button type="button" className="btn inline-flex items-center gap-1 text-primary underline" onClick={() => void openExternalUrl(url)}>
                    Open link
                </button>
            )}
            {Object.keys(group.metadata).length > 0 && (
                <div className="space-y-1 border-t border-border pt-2">
                    {Object.entries(group.metadata).map(([key, value]) => (
                        <div key={key} className="flex gap-2">
                            <span className="font-medium text-muted">{key}:</span>
                            <span className="font-mono text-base-content">{formatMetadataValue(value)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export const providerNoticeRenderer: GroupRenderer<ProviderNoticeGroup> = {
    getLabel: (group) => formatProviderNoticeLabel(group),
    getIcon: (group) => noticeIcon(group),
    getStatusIcon: () => null,
    getHeaderInfo: (group) => <span className="text-xs text-muted">{group.harnessId === "claude-code" ? "Claude Code" : "Codex"}</span>,
    renderContent: (group) => <ProviderNoticeContent group={group} />,
}
